import {
  setCurrentHandOffPartnerRef,
  setLinkedHandOffPartnerInCache,
  setLinkedHandOffPartnersInCache,
} from "../../../../../src/redux/partnerPortal";
import { useAppDispatch, useAppSelector } from "../../../../../src/redux/hooks";
import { useEffect, useMemo, useRef, useState } from "react";

import { HandOffPartner } from "../../__generated__/graphql";
import { gql } from "../../__generated__";
import { requireRef } from "./util";
import useClickOutside from "@verse_core/shared_components/useClickOutside";
import { useQuery } from "@apollo/client";

const GET_LINKED_HAND_OFF_PARTNERS = gql(`
  query LinkedHandOffPartners {
    linkedHandOffPartners {
      id
      ref
      name
      phoneNumber
      email
      faxNumber
      orderPreference
      subcontracted
      versePayorHandoffPortalEnabled
      accessiblePortals
      invoiceEmail
      timezone
      hasPortalAccount
    }
  }
`);

interface PartnerSwitcherProps {
  loggedIn: boolean;
}

export function PartnerSwitcher(props: PartnerSwitcherProps) {
  const dispatch = useAppDispatch();
  const linkedHandOffPartnersCache = useAppSelector(
    (state) => state.partnerPortal.linkedHandOffPartnersCache,
  );

  const { data } = useQuery(GET_LINKED_HAND_OFF_PARTNERS, {
    skip: !props.loggedIn,
  });

  useEffect(() => {
    // Only cache partners that aren't already cached to prevent unnecessary Redux updates
    if (data?.linkedHandOffPartners) {
      const partnersToCache = data.linkedHandOffPartners.filter(
        (partner) => !linkedHandOffPartnersCache[requireRef(partner.ref)],
      );
      if (partnersToCache.length > 0) {
        dispatch(
          setLinkedHandOffPartnersInCache(partnersToCache as HandOffPartner[]),
        );
      }
    }
  }, [data, linkedHandOffPartnersCache, dispatch]);

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use the shared click outside hook
  useClickOutside(containerRef, () => setIsOpen(false));

  // Only compute filtered partners when dropdown is open for performance
  const filteredPartners = useMemo(() => {
    if (!isOpen || !data?.linkedHandOffPartners) {
      return [];
    }

    let partners: HandOffPartner[] =
      data.linkedHandOffPartners as HandOffPartner[];

    // Filter by query if provided
    if (query) {
      partners = partners.filter((partner: HandOffPartner) => {
        return (
          partner.name?.toLowerCase().includes(query.toLowerCase()) ||
          partner.ref?.includes(query) ||
          partner.email?.toLowerCase().includes(query.toLowerCase())
        );
      });
    }

    return partners.slice(0, 200);
  }, [data?.linkedHandOffPartners, query, isOpen]);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
    // Reset search when opening
    if (!isOpen) {
      setQuery("");
    }
  };

  const handlePartnerSelect = (partner: HandOffPartner) => {
    // Cache the partner and switch to it
    dispatch(setLinkedHandOffPartnerInCache(partner));
    dispatch(setCurrentHandOffPartnerRef(requireRef(partner.ref)));
    setIsOpen(false); // Close dropdown after selection
  };

  if (!props.loggedIn || !data || data.linkedHandOffPartners.length === 0) {
    return null;
  }

  const totalPartners = data.linkedHandOffPartners.length;
  const showingCount = filteredPartners.length;
  const isFiltered = query.length > 0;

  return (
    <div className="tw-relative tw-ml-[10px] tw-py-[15px]" ref={containerRef}>
      <button
        className="tw-font-500 tw-rounded-md tw-border-[1px] tw-border-solid tw-border-gray-5 tw-bg-white tw-px-[15px] tw-pb-[10px] tw-pt-[10px] tw-text-[16px] tw-text-black tw-no-underline hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
        onClick={handleToggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <i className="fa fa-handshake"></i>
      </button>
      {isOpen && (
        <div
          className="tw-absolute tw-right-0 tw-z-10 tw-h-[500px] tw-w-[300px] tw-overflow-y-scroll tw-rounded-md tw-border-[0.5px] tw-border-solid tw-border-gray-5 tw-bg-white tw-shadow-lg"
          id="linked-partners-dropdown"
        >
          {data.linkedHandOffPartners.length > 5 && (
            <div className="tw-border-0 tw-border-b-[0.5px] tw-border-solid tw-border-blue-400 tw-bg-gray-300 tw-p-[13px] tw-pb-[8px]">
              <input
                className="tw-w-full tw-rounded-lg tw-border-none tw-p-[13px]"
                type="text"
                placeholder="Search partners"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                autoFocus
              />
              <div className="tw-mt-[8px] tw-text-[8px] tw-text-gray-600">
                {isFiltered
                  ? `Showing ${showingCount} of ${totalPartners} partners`
                  : `Showing first ${showingCount} of ${totalPartners} partners`}
              </div>
            </div>
          )}
          {filteredPartners.map((partner: HandOffPartner, i: number) => (
            <button
              className="tw-m-0 tw-block tw-w-full tw-appearance-none tw-border-none tw-bg-transparent tw-p-0 tw-text-left hover:tw-bg-light-grey"
              onClick={() => handlePartnerSelect(partner)}
              key={i}
            >
              <div className="tw-cursor-pointer tw-border-0 tw-border-b tw-border-solid tw-border-blue-400 tw-p-[13px]">
                <div className="tw-text-custom-blue">{partner.name}</div>
                {partner.email && (
                  <div className="tw-mt-[5px] tw-text-gray-600">
                    {partner.email}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
